import { Truthy } from 'lodash';

import { graphql } from '@/__generated__';
import { AppliedModelSort, ModelAppliedFilter } from '@/__generated__/graphql';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';

export type ModelAppliedListNode = Truthy<
  ReturnType<typeof useDiscoveryAppliedModels>['data']
>[0];

export type ModelWithQueryCount = ModelAppliedListNode & {
  usageQueryCount?: number;
};

export const GetModelListByUniqueId = graphql(/* GraphQL */ `
  query AppliedModels(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: ModelAppliedFilter
    $sort: AppliedModelSort
    $includeQueryCount: Boolean!
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        models(first: $first, after: $after, filter: $filter, sort: $sort) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              projectId
              environmentId
              uniqueId
              name
              packageName
              releaseVersion
              access
              contractEnforced
              executionInfo {
                lastRunId
                lastRunStatus
                lastSuccessRunId
                executeCompletedAt
              }
              tags
              group
              fqn
              alias
              database
              schema
              healthIssues
              modelingLayer
              usageQueryCount(age: 30) @include(if: $includeQueryCount)
              catalog {
                rowCountStat
                bytesStat
                stats {
                  id
                  label
                  value
                  description
                  include
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const GetModelListByUniqueIdWithoutQueryHistory = graphql(/* GraphQL */ `
  query AppliedModelsWithoutQueryHistory(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: ModelAppliedFilter
    $sort: AppliedModelSort
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        models(first: $first, after: $after, filter: $filter, sort: $sort) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              projectId
              environmentId
              uniqueId
              name
              packageName
              releaseVersion
              access
              contractEnforced
              executionInfo {
                lastRunId
                lastRunStatus
                lastSuccessRunId
                executeCompletedAt
              }
              tags
              group
              fqn
              alias
              database
              schema
              healthIssues
              modelingLayer
              catalog {
                rowCountStat
                bytesStat
                stats {
                  id
                  label
                  value
                  description
                  include
                }
              }
            }
          }
        }
      }
    }
  }
`);

export type AppliedModelsByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  includeQueryCount: boolean;
  filter?: Partial<ModelAppliedFilter>;
  sort?: Partial<AppliedModelSort>;
  pause?: boolean;
};

export const QUERY_USAGE_TOOLTIP =
  'The number of consumed queries against this resource for the last 30 days';

export const useDiscoveryAppliedModels = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  sort,
  includeQueryCount = false,
  pause,
}: AppliedModelsByEnvironmentParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const queryChoice = hasAccountFeature('explorerQueryHistory')
    ? GetModelListByUniqueId
    : GetModelListByUniqueIdWithoutQueryHistory;
  const query = usePaginatedQuery(
    {
      query: queryChoice,
      variables: {
        first,
        after,
        environmentId,
        userId: userId as number | null,
        filter,
        sort,
        includeQueryCount,
      },
      pause: pause || !environmentId || !first || userId === 'loading',
    },
    (data) => {
      const models = data.environment.applied?.models;
      const pageInfo = models?.pageInfo;
      if (models == null || pageInfo == null) return undefined;
      return {
        totalCount: models.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return (data.environment.applied?.models.edges || [])
        .map((edge) => edge.node)
        .filter(truthy);
    },
    (data) => data.environment.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  return query;
};
